import axios from "axios";
import { hideChildModal, hideModal } from "./modal";

/*****************/
/* INITIAL STATE */
/*****************/
const initialState = {
  loading: false,
  error: "",
  data: [],
};

/*********/
/* TYPES */
/*********/
const FETCH_USERS = "FETCH_USERS";
const FETCH_USERS_SUCCESS = "FETCH_USERS_SUCCESS";
const FETCH_USERS_ERROR = "FETCH_USERS_ERROR";
const FETCH_NEXT_USERS = "FETCH_NEXT_USERS";
const FETCH_NEXT_USERS_SUCCESS = "FETCH_NEXT_USERS_SUCCESS";
const DELETE_USER = "DELETE_USER";
const CREATE_USER = "CREATE_USER";
const CREATE_USER_SUCCESS = "CREATE_USER_SUCCESS";
const CREATE_USER_ERROR = "CREATE_USER_ERROR";
const UPDATE_USER = "UPDATE_USER";
const UPDATE_USER_SUCCESS = "UPDATE_USER_SUCCESS";
const UPDATE_USER_ERROR = "UPDATE_USER_ERROR";

/*******************/
/* ACTION CREATORS */
/*******************/
export const getUsers = () => async (dispatch) => {
  dispatch(loadUsers());

  const response = await axios({
    method: "get",
    url: `${process.env.REACT_APP_API_URL}/users?page=1`,
    headers: {
      "Content-Type": "application/ld+json; charset=utf-8",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

  if (response.data["hydra:member"].length === 0) {
    dispatch(setError("No users found"));
  } else {
    dispatch(setUsers(response.data["hydra:member"]));
    if (
      response.data &&
      response.data["hydra:view"]?.["hydra:next"]
    ) {
      dispatch(getNextUsers(response.data["hydra:view"]["hydra:next"]));
    }
  }
};

export const getNextUsers = (nextUrl) => async (dispatch) => {
  dispatch(loadNextUsers());

  const response = await axios({
    method: "get",
    url: `${process.env.REACT_APP_URL}${nextUrl}`,
    headers: {
      "Content-Type": "application/ld+json; charset=utf-8",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

  dispatch(setNextUsers(response.data["hydra:member"]));
  if (
    response.data &&
    response.data["hydra:view"]?.["hydra:next"]
  ) {
    dispatch(getNextUsers(response.data["hydra:view"]["hydra:next"]));
  }
};

export const loadUsers = () => ({ type: FETCH_USERS });

export const setUsers = (users) => ({
  type: FETCH_USERS_SUCCESS,
  payload: { users },
});

export const loadNextUsers = (page) => ({
  type: FETCH_NEXT_USERS,
  payload: page,
});
export const setNextUsers = (users) => ({
  type: FETCH_NEXT_USERS_SUCCESS,
  payload: users,
});

export const setError = (msg) => ({
  type: FETCH_USERS_ERROR,
  payload: msg,
});

export const deleteUserAction = () => ({ type: DELETE_USER });

export const removeUser = (user) => async (dispatch, getState) => {
  dispatch(deleteUserAction());
  const {
    users: { data },
    modal: modal
  } = getState();

  const response = await axios({
    method: "delete",
    url: `${process.env.REACT_APP_URL}${user["@id"] || `/users/${user.id}`}`,
    headers: {
      "Content-Type": "application/ld+json; charset=utf-8",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

  if (response.status === 204) {
    const updated = data.filter(
      (u) => u["@id"] !== (user["@id"] || `/users/${user.id}`)
    );

    dispatch(setUsers(updated));
   
    if (modal.childModal) {
      dispatch(hideChildModal());
    } else {
      dispatch(hideModal());
    }
  } else {
    dispatch(setError("Something went wrong deleting user"));
  }
};

export const createUser = (user, callback) => async (dispatch, getState) => {
  dispatch({ type: CREATE_USER });

  const {
    modal: modal
  } = getState();

  try {
    const response = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/users`,
      headers: {
        "Content-Type": "application/ld+json; charset=utf-8",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      data: user,
    });

    dispatch({
      type: CREATE_USER_SUCCESS,
      payload: response.data,
    });

    if (callback) {
      callback("users", [response.data["@id"]]);
    }

    if (modal.childModal) {
      dispatch(hideChildModal());
    } else {
      dispatch(hideModal());
    }
  } catch (error) {
    dispatch({
      type: CREATE_USER_ERROR,
      payload: error.message || "Failed to create user",
    });
  }
};

export const updateUser = (user) => async (dispatch, getState) => {
  dispatch({ type: UPDATE_USER });

  const {
    modal: modal
  } = getState();

  try {
    const response = await axios({
      method: "put",
      url: `${process.env.REACT_APP_URL}${user["@id"]}`,
      headers: {
        "Content-Type": "application/ld+json; charset=utf-8",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      data: user,
    });

    dispatch({
      type: UPDATE_USER_SUCCESS,
      payload: response.data,
    });
    
    if (modal.childModal) {
      dispatch(hideChildModal());
    } else {
      dispatch(hideModal());
    }
  } catch (error) {
    dispatch({
      type: UPDATE_USER_ERROR,
      payload: error.message || "Failed to update user",
    });
  }
};

/***********/
/* REDUCER */
/***********/
const usersReducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case FETCH_USERS:
      return {
        ...state,
        loading: true,
        error: "",
      };
    case FETCH_USERS_SUCCESS:
      return {
        ...state,
        loading: false,
        error: "",
        data: payload.users,
      };
    case FETCH_USERS_ERROR:
      return {
        ...state,
        loading: false,
        error: payload,
      };
    case FETCH_NEXT_USERS:
      return {
        ...state,
        error: "",
      };
    case FETCH_NEXT_USERS_SUCCESS:
      return {
        ...state,
        loading: false,
        data: [...state.data, ...payload],
      };
    case DELETE_USER:
      return {
        ...state,
        loading: true,
        error: "",
      };
    case CREATE_USER:
      return {
        ...state,
        loading: true,
        error: "",
      };

    case CREATE_USER_SUCCESS:
      return {
        ...state,
        loading: false,
        data: [payload, ...state.data],
      };

    case CREATE_USER_ERROR:
      return {
        ...state,
        loading: false,
        error: payload,
      };
     case UPDATE_USER:
       return {
         ...state,
         loading: true,
         error: "",
       };

     case UPDATE_USER_SUCCESS:
       return {
        ...state,
        loading: false,
        data: state.data.map((u) =>
          u["@id"] === payload["@id"] ? payload : u
        ),
      };

     case UPDATE_USER_ERROR:
       return {
         ...state,
         loading: false,
         error: payload,
       };
    default:
      return state;
  }
};

export default usersReducer;
